// rank2-roots.js — finite rank-2 root systems generated by reflection closure.
//
// These systems are small enough to construct live. Starting from two simple
// roots, repeatedly reflect every discovered root in both simple-root mirrors.
// The result is the full A2, B2, G2, or non-crystallographic H2/I2(5) system.

const RANK2_PHI = (1 + Math.sqrt(5)) / 2;
const EPSILON = 1e-8;

export const RANK2_ROOT_SYSTEM_ORDER = Object.freeze(['A2', 'B2', 'G2', 'H2']);

export const RANK2_ROOT_SYSTEMS = Object.freeze({
  A2: Object.freeze({
    id: 'A2',
    label: 'A₂',
    name: 'Hexagonal symmetry',
    family: 'I₂(3)',
    coxeterNumber: 3,
    rootCount: 6,
    longToShortRatio: 1,
    crystallographic: true,
    bond: 1,
    description: 'Six equal roots form the symmetry behind the equilateral triangle and hexagonal lattice.',
  }),
  B2: Object.freeze({
    id: 'B2',
    label: 'B₂',
    name: 'Square symmetry',
    family: 'I₂(4)',
    coxeterNumber: 4,
    rootCount: 8,
    longToShortRatio: Math.SQRT2,
    crystallographic: true,
    bond: 2,
    description: 'Four long and four short roots encode the reflection symmetry of the square.',
  }),
  G2: Object.freeze({
    id: 'G2',
    label: 'G₂',
    name: 'Twelve-root symmetry',
    family: 'I₂(6)',
    coxeterNumber: 6,
    rootCount: 12,
    longToShortRatio: Math.sqrt(3),
    crystallographic: true,
    bond: 3,
    description: 'Two interlaced hexagons of long and short roots form the smallest exceptional root system.',
  }),
  H2: Object.freeze({
    id: 'H2',
    label: 'H₂',
    name: 'Golden symmetry',
    family: 'I₂(5)',
    coxeterNumber: 5,
    rootCount: 10,
    longToShortRatio: 1,
    crystallographic: false,
    bond: 5,
    description: 'Ten equal roots reveal fivefold, golden-ratio symmetry outside the crystallographic families.',
  }),
});

export function reflectAcrossRoot(vector, root) {
  const denominator = dot(root, root);
  if (denominator <= EPSILON) return [...vector];
  const factor = 2 * dot(vector, root) / denominator;
  return [vector[0] - factor * root[0], vector[1] - factor * root[1]];
}

export function rank2SimpleRoots(systemId) {
  const meta = RANK2_ROOT_SYSTEMS[systemId] || RANK2_ROOT_SYSTEMS.A2;
  const m = meta.coxeterNumber;
  const longLength = meta.longToShortRatio;
  const shortLength = 1;
  const angle = Math.PI - Math.PI / m;
  return [
    [longLength, 0],
    [shortLength * Math.cos(angle), shortLength * Math.sin(angle)],
  ];
}

export function generateRank2RootSystem(systemId = 'A2') {
  const meta = RANK2_ROOT_SYSTEMS[systemId] || RANK2_ROOT_SYSTEMS.A2;
  const simpleRoots = rank2SimpleRoots(meta.id);
  const roots = reflectionClosure(simpleRoots);
  const maxLength = Math.max(...roots.map(length));
  const normalizedRoots = roots
    .map((root, index) => ({
      id: index,
      vector: root,
      point: [root[0] / maxLength, root[1] / maxLength],
      length: length(root),
      kind: Math.abs(length(root) - maxLength) < 1e-6 ? 'long' : 'short',
      angle: positiveAngle(Math.atan2(root[1], root[0])),
    }))
    .sort((a, b) => a.angle - b.angle)
    .map((root, index) => ({ ...root, id: index }));
  const cartanMatrix = simpleRoots.map(row => simpleRoots.map(column => (
    cleanNumber(2 * dot(row, column) / dot(column, column))
  )));
  const longRootCount = normalizedRoots.filter(root => root.kind === 'long').length;
  const shortRootCount = normalizedRoots.length - longRootCount;
  return {
    ...meta,
    simpleRoots,
    roots: normalizedRoots,
    cartanMatrix,
    chamberAngle: Math.PI / meta.coxeterNumber,
    chamberAngleDegrees: 180 / meta.coxeterNumber,
    chamberCount: meta.rootCount,
    simpleAngleDegrees: 180 - 180 / meta.coxeterNumber,
    longRootCount,
    shortRootCount,
    goldenRatio: meta.id === 'H2' ? RANK2_PHI : null,
  };
}

export function coxeterOrbit(systemId = 'A2', seed = null) {
  const system = generateRank2RootSystem(systemId);
  const [alpha1, alpha2] = system.simpleRoots;
  const start = seed ? [...seed] : [...alpha1];
  const orbit = [];
  let current = start;
  for (let step = 0; step < system.coxeterNumber; step++) {
    orbit.push(current);
    current = reflectAcrossRoot(reflectAcrossRoot(current, alpha2), alpha1);
  }
  return orbit;
}

function reflectionClosure(simpleRoots) {
  const roots = [];
  const queue = simpleRoots.flatMap(root => [root, [-root[0], -root[1]]]);
  while (queue.length) {
    const candidate = queue.shift();
    if (roots.some(root => nearlyEqual(root, candidate))) continue;
    roots.push(candidate);
    for (const mirror of simpleRoots) {
      const reflected = reflectAcrossRoot(candidate, mirror);
      if (!roots.some(root => nearlyEqual(root, reflected))) queue.push(reflected);
    }
    if (roots.length > 24) throw new Error('Rank-2 reflection closure did not terminate');
  }
  return roots;
}

function dot(a, b) {
  return a[0] * b[0] + a[1] * b[1];
}

function length(vector) {
  return Math.hypot(vector[0], vector[1]);
}

function nearlyEqual(a, b) {
  return Math.abs(a[0] - b[0]) < EPSILON && Math.abs(a[1] - b[1]) < EPSILON;
}

function positiveAngle(angle) {
  return angle < 0 ? angle + Math.PI * 2 : angle;
}

function cleanNumber(number) {
  const rounded = Math.round(number);
  return Math.abs(number - rounded) < 1e-9 ? rounded : Number(number.toFixed(12));
}
